import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SmsButton } from "@/components/SendSmsDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RichTextEditor, sanitizeHtml } from "@/components/RichTextEditor";
import { LoginAccessCard } from "@/components/LoginAccessCard";
import { BulkLoginsDialog } from "@/components/BulkLoginsDialog";
import { todayISO } from "@/lib/format";
import FinalDateDialog from "@/components/worker/FinalDateDialog";
import { Combobox } from "@/components/Combobox";
import {
  IdCard, Search, Loader2, ArrowLeft, Save, Plus, Trash2, Upload, FileText,
  AlertTriangle, ShieldCheck, ShieldAlert, Clock, Users, CheckCircle2,
  UserPlus, Download, FileDown, FilePlus, Archive, ChevronDown, RotateCcw,
  History, Pencil, Lock, UsersRound, LogOut,
} from "lucide-react";
import { toast } from "sonner";
import { fmtLog, fmtLogDate } from "@/lib/tz";
import { AddressField } from "@/components/AddressField";
import { orgNowLocal } from "@/lib/tz";
import { byNewest, bySoonestExpiry } from "@/lib/staffDocOrder";

const CRIMSON = "#bf3b4f";
const EXPIRING_DAYS = 60;

function statusOf(expiry) {
  if (!expiry) return { key: "current", days: null };
  const d = new Date(expiry + "T00:00:00");
  const today = orgNowLocal().startOf("day").toDate();
  const days = Math.round((d - today) / 86400000);
  if (days < 0) return { key: "expired", days };
  if (days <= EXPIRING_DAYS) return { key: "expiring", days };
  return { key: "current", days };
}

// Staff-record tab where each document category is added.
const MISSING_DOC_TAB = { clearance: "clearances", skill: "skills", confidential: "confidential" };

const STATUS_STYLE = {
  expired: "bg-rose-50 text-rose-700 border-rose-200",
  expiring: "bg-amber-50 text-amber-700 border-amber-200",
  current: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtDate(v) {
  if (!v) return "—";
  const s = String(v).trim();
  // Date-only (YYYY-MM-DD): parse at LOCAL midnight so the calendar date never
  // shifts due to the browser's timezone. Datetimes are parsed as given.
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s.replace(" ", "T");
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

// Sort (newest-first by added_at unless a comparator is given) and paginate; returns the current
// page's items (each carrying its original index) plus pager state. Pager hides below one page.
function usePaged(rows, defaultSize = 15, compare = byNewest) {
  const [pageSize, setPageSize] = useState(defaultSize);
  const [page, setPage] = useState(1);
  const ordered = useMemo(
    () => rows.map((r, i) => ({ r, i })).sort((a, b) => compare(a.r, b.r)),
    [rows, compare]
  );
  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount);
  const slice = ordered.slice((current - 1) * pageSize, current * pageSize);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [pageCount]);
  return { slice, total, page: current, setPage, pageSize, setPageSize, pageCount };
}

function Pager({ total, page, setPage, pageSize, setPageSize, pageCount, testid }) {
  if (total <= 10) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-slate-100" data-testid={`${testid}-pager`}>
      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        <span>Show</span>
        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
          <SelectTrigger className="h-7 w-16 text-xs" data-testid={`${testid}-pagesize`}><SelectValue /></SelectTrigger>
          <SelectContent>{[10, 15, 25].map((n) => <SelectItem key={n} value={String(n)} className="text-xs">{n}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-slate-400">·&nbsp;{from}–{to} of {total}</span>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}>Prev</Button>
        <span className="text-[11px] text-slate-500 tabular-nums px-1">{page}/{pageCount}</span>
        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={page >= pageCount} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}>Next</Button>
      </div>
    </div>
  );
}

function ComplianceBadge({ counts }) {
  if (counts.expired > 0) return <Badge className={`${STATUS_STYLE.expired} text-[10px]`} data-testid="badge-expired">{counts.expired} expired</Badge>;
  if (counts.expiring > 0) return <Badge className={`${STATUS_STYLE.expiring} text-[10px]`} data-testid="badge-expiring">{counts.expiring} expiring</Badge>;
  return <Badge className={`${STATUS_STYLE.current} text-[10px]`} data-testid="badge-current">All current</Badge>;
}

export default function StaffRecords() {
  const [overview, setOverview] = useState(null);
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [sel, setSel] = useState(null);
  const [selTab, setSelTab] = useState("profile");
  const [error, setError] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const { can } = useAuth();
  const [exporting, setExporting] = useState(null);

  const load = () => {
    Promise.all([api.hrOverview(), api.hrStaffList()])
      .then(([ov, ls]) => { setOverview(ov); setList(ls); })
      .catch((e) => { if (e.response?.status === 403) setError("no-access"); else setError(true); });
  };
  useEffect(() => { load(); }, []);

  const runExport = async (fmt) => {
    setExporting(fmt);
    try {
      const blob = fmt === "csv" ? await api.hrExportCsv() : await api.hrExportPdf();
      const stamp = todayISO();
      downloadBlob(blob, `compliance-needs-attention-${stamp}.${fmt}`);
    } catch { toast.error("Export failed."); }
    finally { setExporting(null); }
  };

  const filtered = useMemo(() => {
    if (!list) return [];
    const t = q.trim().toLowerCase();
    return list.filter((s) =>
      (statusFilter === "All" || s.status === statusFilter) &&
      ((s.display_name || "").toLowerCase().includes(t) || (s.primary_role || "").toLowerCase().includes(t))
    );
  }, [list, q, statusFilter]);

  if (error === "no-access") return <div className="p-8 text-sm text-slate-500" data-testid="hr-no-access">You don't have access to HR staff records.</div>;

  const openStaff = (name, tab = "profile") => { setSelTab(tab); setSel(name); };
  const missingCritical = overview?.missing_critical || [];
  const attentionCount = (overview?.items.length || 0) + missingCritical.length;

  if (sel) return <StaffDetail name={sel} initialTab={selTab} onBack={() => { setSel(null); load(); }} />;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="staff-records-page">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="flex items-center gap-2.5">
            <IdCard className="h-5 w-5" style={{ color: CRIMSON }} />
            <h1 className="text-xl font-semibold font-brand text-slate-800">Staff Records</h1>
          </div>
          <p className="text-xs text-slate-500 mt-1">Worker profiles, clearances and training — with compliance expiry tracking.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => runExport("csv")} disabled={exporting !== null} className="h-9 text-xs" data-testid="export-csv-btn">
            {exporting === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => runExport("pdf")} disabled={exporting !== null} className="h-9 text-xs" data-testid="export-pdf-btn">
            {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />}PDF
          </Button>
          {can.has("hr.edit") && (
            <Button size="sm" onClick={() => setShowAdd(true)} style={{ backgroundColor: CRIMSON }} className="h-9 text-xs text-white" data-testid="add-staff-btn">
              <UserPlus className="h-3.5 w-3.5 mr-1.5" />Add staff
            </Button>
          )}
          {can.has("hr.logins") && (
            <Button variant="outline" size="sm" onClick={() => setShowBulk(true)} className="h-9 text-xs" data-testid="bulk-logins-btn">
              <UsersRound className="h-3.5 w-3.5 mr-1.5" />Bulk logins
            </Button>
          )}
        </div>
      </div>

      {showAdd && <AddStaffDialog onClose={() => setShowAdd(false)} onCreated={(name) => { setShowAdd(false); load(); if (name) openStaff(name); }} />}
      <BulkLoginsDialog open={showBulk} onOpenChange={setShowBulk} onDone={load} />

      {/* Compliance overview */}
      {overview === null ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : (
        <div className="mb-6" data-testid="compliance-overview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatCard icon={Users} label="Active staff" value={overview.active_staff} tone="slate" />
            <StatCard icon={CheckCircle2} label="Fully compliant" value={overview.fully_compliant} tone="emerald" />
            <StatCard icon={Clock} label={`Expiring ≤${EXPIRING_DAYS}d`} value={overview.expiring} tone="amber" />
            <StatCard icon={ShieldAlert} label="Expired" value={overview.expired} tone="rose" />
          </div>
          {attentionCount > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white">
              <div className="px-4 py-2.5 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />Needs attention ({attentionCount})
              </div>
              {missingCritical.length > 0 && (
                <div className="border-b border-slate-100" data-testid="missing-critical">
                  <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-rose-600 flex items-center gap-1.5">
                    <ShieldAlert className="h-3 w-3" />Missing critical documents ({missingCritical.length})
                    <span className="normal-case tracking-normal font-normal text-slate-400">· can't be rostered until added</span>
                  </div>
                  <div className="max-h-56 overflow-y-auto divide-y divide-slate-50">
                    {missingCritical.map((m) => (
                      <div key={m.staff} className="flex items-center justify-between gap-3 px-4 py-2" data-testid={`missing-${m.staff}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{m.staff_name}</div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {m.documents.map((d) => (
                              <Badge key={d.document_type} className={`${STATUS_STYLE.expired} text-[10px]`}>{d.document_type}</Badge>
                            ))}
                          </div>
                        </div>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs shrink-0"
                          onClick={() => openStaff(m.staff, MISSING_DOC_TAB[m.documents[0]?.category] || "clearances")}
                          data-testid={`missing-add-${m.staff}`}>
                          <Plus className="h-3 w-3 mr-1" />Add documents
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {missingCritical.length > 0 && overview.items.length > 0 && (
                <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-amber-600 flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />Expired or expiring ({overview.items.length})
                </div>
              )}
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-50">
                {overview.items.map((it, i) => (
                  <button key={i} onClick={() => openStaff(it.staff)} data-testid={`flag-${it.staff}-${i}`}
                    className="w-full flex items-center justify-between gap-3 px-4 py-2 text-left hover:bg-slate-50 transition-colors">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{it.staff_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{it.category} · {it.document_type}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] text-slate-400">{it.expiry_date}</span>
                      <Badge className={`${STATUS_STYLE[it.status]} text-[10px]`}>
                        {it.status === "expired" ? `expired ${Math.abs(it.days_until)}d ago` : `in ${it.days_until}d`}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Staff list */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff or role…" className="h-9 pl-8 text-sm" data-testid="staff-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-36 text-sm" data-testid="staff-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["Active", "Inactive", "Left", "All"].map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {list === null ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-50" data-testid="staff-list">
          {filtered.map((s) => (
            <button key={s.name} onClick={() => openStaff(s.name)} data-testid={`staff-row-${s.name}`}
              className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{s.display_name}</div>
                <div className="text-[11px] text-slate-500 truncate">{s.primary_role || "—"}{s.status && s.status !== "Active" ? ` · ${s.status}` : ""}</div>
              </div>
              <ComplianceBadge counts={s.compliance} />
            </button>
          ))}
          {filtered.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">No staff match.</div>}
        </div>
      )}
    </div>
  );
}

function ProfileSection({ title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">{title}</div>
      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">{children}</div>
    </div>
  );
}

function PField({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


function StatCard({ icon: Icon, label, value, tone }) {
  const tones = {
    slate: "text-slate-600 bg-slate-50 border-slate-200",
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-200",
    amber: "text-amber-600 bg-amber-50 border-amber-200",
    rose: "text-rose-600 bg-rose-50 border-rose-200",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider opacity-80"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function StaffDetail({ name, initialTab = "profile", onBack }) {
  const { can, refreshUser } = useAuth();
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const [meta, setMeta] = useState(null);
  const photoInputRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [markFinalDateDialog, setMarkFinalDateDialog] = useState(false);
  // Your own record is basic details only and view-only without hr.own_record (the server enforces it).
  const own = !!doc?.own_record;
  const canEdit = can.has("hr.edit") && !own;

  const reload = () => api.hrStaff(name).then((d) => { setDoc(d); setForm(d); });
  useEffect(() => {
    reload().catch(() => toast.error("Could not load staff record."));
  }, [name]);
  useEffect(() => {
    api.hrDocTypes().then((d) => setDocTypes(d.types || [])).catch(() => {});
    api.hrMeta().then(setMeta).catch(() => {});
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhotoPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setPhotoBusy(true);
    try {
      const res = await api.hrUploadPhoto(name, f);
      setDoc((d) => ({ ...d, profile_photo: res.photo }));
      refreshUser();
      toast.success("Profile photo updated.");
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't upload that photo."); }
    finally { setPhotoBusy(false); }
  };
  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      await api.hrDeletePhoto(name);
      setDoc((d) => ({ ...d, profile_photo: null }));
      refreshUser();
      toast.success("Profile photo removed.");
    } catch { toast.error("Couldn't remove the photo."); }
    finally { setPhotoBusy(false); }
  };

  // Notes save straight away (not via the record's Save button) so a note is never
  // lost by leaving the page; other unsaved edits on the form are left untouched.
  const saveNotes = async (rows) => {
    try {
      await api.hrStaffUpdate(name, { notes: noteRows(rows) });
      setField("notes", rows);
      setDoc((d) => ({ ...d, notes: rows }));
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the note.");
      return false;
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        first_name: form.first_name, last_name: form.last_name,
        preferred_name: form.preferred_name, status: form.status,
        gender: form.gender, pronouns: form.pronouns, cell_number: form.cell_number,
        date_of_birth: form.date_of_birth, date_of_joining: form.date_of_joining, reports_to: form.reports_to,
        company_email: form.company_email, personal_email: form.personal_email,
        languages: form.languages, address: form.address,
        hobbies: form.hobbies, about_me: form.about_me, rostering_considerations: form.rostering_considerations,
        emer_name: form.emer_name, emer_phone: form.emer_phone, emer_relation: form.emer_relation,
        notes: noteRows(form.notes),
      };
      await api.hrStaffUpdate(name, body);
      toast.success("Staff record saved.");
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  if (!doc) return <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="staff-detail">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="staff-back"><ArrowLeft className="h-4 w-4" />Back to staff</button>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-4">
          {doc.profile_photo
            ? <img src={doc.profile_photo} alt={doc.display_name || doc.name} className="h-16 w-16 rounded-full object-cover border border-slate-200 shrink-0" data-testid="staff-photo" />
            : <div className="h-16 w-16 rounded-full flex items-center justify-center text-lg font-semibold text-white shrink-0" style={{ backgroundColor: CRIMSON }} data-testid="staff-photo-fallback">{(doc.display_name || doc.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</div>}
          <div>
            <h1 className="text-xl font-semibold font-brand text-slate-800">{doc.display_name || doc.name}</h1>
            <div className="text-xs text-slate-500 mt-0.5">{doc.primary_role || "—"} · {doc.employee_name || doc.employee || doc.name}</div>
            <div className="flex items-center gap-3 mt-1.5">
              {canEdit && <>
              <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoPick} className="hidden" data-testid="staff-photo-input" />
              <button onClick={() => photoInputRef.current?.click()} disabled={photoBusy} className="text-xs inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 disabled:opacity-50" data-testid="staff-photo-upload">
                {photoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}{doc.profile_photo ? "Change photo" : "Add photo"}
              </button>
              {doc.profile_photo && <button onClick={removePhoto} disabled={photoBusy} className="text-xs inline-flex items-center gap-1 text-rose-600 hover:text-rose-800 disabled:opacity-50" data-testid="staff-photo-remove"><Trash2 className="h-3.5 w-3.5" />Remove</button>}
              </>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(form.cell_number || doc.cell_number) && (
            <SmsButton type="staff" id={doc.name} name={doc.display_name || doc.name}
              mobile={form.cell_number || doc.cell_number} />
          )}
          {canEdit && (
            <Button onClick={save} disabled={saving} style={{ backgroundColor: CRIMSON }} data-testid="staff-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save
            </Button>
          )}
          {can.has("hr.offboard") && !own && form.status === "Active" && (
            <Button
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={() => setMarkFinalDateDialog(true)}
              data-testid="staff-mark-final-date"
            >
              <LogOut className="h-4 w-4 mr-1.5" />Mark Final Date
            </Button>
          )}
        </div>
      </div>

      <FinalDateDialog
        open={markFinalDateDialog}
        onOpenChange={setMarkFinalDateDialog}
        employee={name}
        employeeName={doc?.employee_name || doc?.name}
        onSuccess={() => {
          toast.success("Worker final date processed successfully.");
          reload();
          refreshUser();
        }}
      />

      {own && (
        <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="staff-own-record">
          This is your own staff record, so you can see its basic details only. Ask HR to make any changes.
        </div>
      )}
      <Tabs defaultValue={own && ["confidential", "notes"].includes(initialTab) ? "profile" : initialTab}>
        <TabsList data-testid="staff-tabs">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="clearances" data-testid="tab-clearances">Clearances & Checks</TabsTrigger>
          <TabsTrigger value="skills" data-testid="tab-skills">Skills & Training</TabsTrigger>
          {!own && <TabsTrigger value="confidential" data-testid="tab-confidential">Confidential</TabsTrigger>}
          {!own && <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <fieldset disabled={!canEdit} className="space-y-4 min-w-0" data-testid="staff-profile-fields">
          <ProfileSection title="Identity & role">
            <PField label="First name"><Input value={form.first_name || ""} onChange={(e) => setField("first_name", e.target.value)} className="h-9 text-sm" data-testid="sf-first_name" /></PField>
            <PField label="Last name"><Input value={form.last_name || ""} onChange={(e) => setField("last_name", e.target.value)} className="h-9 text-sm" data-testid="sf-last_name" /></PField>
            <PField label="Preferred name"><Input value={form.preferred_name || ""} onChange={(e) => setField("preferred_name", e.target.value)} className="h-9 text-sm" placeholder="Overrides first name in display" data-testid="sf-preferred_name" /></PField>
            <PField label="Pronouns"><Input value={form.pronouns || ""} onChange={(e) => setField("pronouns", e.target.value)} className="h-9 text-sm" data-testid="sf-pronouns" /></PField>
            <PField label="Gender">
              <Select value={form.gender || ""} onValueChange={(v) => setField("gender", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="sf-gender"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{(meta?.genders || []).map((g) => <SelectItem key={g} value={g} className="text-sm">{g}</SelectItem>)}</SelectContent>
              </Select>
            </PField>
            <PField label="Date of birth"><Input type="date" value={(form.date_of_birth || "").slice(0, 10)} onChange={(e) => setField("date_of_birth", e.target.value)} className="h-9 text-sm" data-testid="sf-date_of_birth" /></PField>
            <PField label="Date joined"><Input type="date" value={(form.date_of_joining || "").slice(0, 10)} onChange={(e) => setField("date_of_joining", e.target.value)} className="h-9 text-sm" data-testid="sf-date_of_joining" /></PField>
            <PField label="Reports to">
              <Combobox options={(meta?.employees || []).map((e) => ({ value: e.name, label: e.employee_name }))}
                value={form.reports_to || null} onChange={(v) => setField("reports_to", v)} placeholder="Select manager…" testid="sf-reports_to" />
            </PField>
            <PField label="Role (Designation)"><Input value={form.primary_role || ""} disabled title="Set in HR (Employee Designation)" className="h-9 text-sm bg-slate-50 text-slate-500" data-testid="sf-primary_role" /></PField>
            <PField label="Status">
              <Select value={form.status || ""} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="sf-status"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{(meta?.statuses || ["Active", "Inactive", "Suspended", "Left"]).map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
              </Select>
            </PField>
          </ProfileSection>

          <ProfileSection title="Contact">
            <PField label="Mobile"><Input value={form.cell_number || ""} onChange={(e) => setField("cell_number", e.target.value)} className="h-9 text-sm" data-testid="sf-cell_number" /></PField>
            <PField label="Work email"><Input value={form.company_email || ""} onChange={(e) => setField("company_email", e.target.value)} className="h-9 text-sm" data-testid="sf-company_email" /></PField>
            <PField label="Personal email"><Input value={form.personal_email || ""} onChange={(e) => setField("personal_email", e.target.value)} className="h-9 text-sm" data-testid="sf-personal_email" /></PField>
          </ProfileSection>

          <ProfileSection title="Address">
            <div className="sm:col-span-2">
              <Label className="text-[11px] text-slate-500">Current address</Label>
              <div className="mt-1">
                <AddressField value={form.address || ""} onChange={(v) => setField("address", v)} testid="sf-address" />
              </div>
            </div>
          </ProfileSection>

          <ProfileSection title="Emergency contact">
            <PField label="Name"><Input value={form.emer_name || ""} onChange={(e) => setField("emer_name", e.target.value)} className="h-9 text-sm" data-testid="sf-emer_name" /></PField>
            <PField label="Phone"><Input value={form.emer_phone || ""} onChange={(e) => setField("emer_phone", e.target.value)} className="h-9 text-sm" data-testid="sf-emer_phone" /></PField>
            <PField label="Relationship"><Input value={form.emer_relation || ""} onChange={(e) => setField("emer_relation", e.target.value)} className="h-9 text-sm" data-testid="sf-emer_relation" /></PField>
          </ProfileSection>

          <ProfileSection title="Bio">
            <Field label="Languages spoken"><Input value={form.languages || ""} onChange={(e) => setField("languages", e.target.value)} className="h-9 text-sm" data-testid="sf-languages" /></Field>
            <Field label="Hobbies & interests"><Input value={form.hobbies || ""} onChange={(e) => setField("hobbies", e.target.value)} className="h-9 text-sm" data-testid="sf-hobbies" /></Field>
            <div className="sm:col-span-2">
              <Label className="text-[11px] text-slate-500">About me</Label>
              <div className="mt-1"><RichTextEditor value={form.about_me || ""} onChange={(v) => setField("about_me", v)} disabled={!canEdit} testid="sf-about_me" /></div>
            </div>
            {!own && <div className="sm:col-span-2">
              <Label className="text-[11px] text-slate-500">Rostering considerations</Label>
              <div className="mt-1"><RichTextEditor value={form.rostering_considerations || ""} onChange={(v) => setField("rostering_considerations", v)} disabled={!canEdit} testid="sf-rostering" /></div>
            </div>}
          </ProfileSection>
          </fieldset>
          {can.has("hr.logins") && !own && <LoginAccessCard employee={name} suggestedEmail={form.company_email || form.personal_email} />}
        </TabsContent>

        <TabsContent value="clearances" className="mt-4">
          <DocTable rows={form.active_compliance || []} staffName={name} testid="clearances"
            category="clearance" isAdmin={!own && can.has("hr.delete")} canEdit={canEdit} own={own} reload={reload}
            docTypes={docTypes.filter((t) => t.category === "clearance")} />
        </TabsContent>
        <TabsContent value="skills" className="mt-4">
          <DocTable rows={form.active_skills || []} staffName={name} testid="skills"
            category="skill" isAdmin={!own && can.has("hr.delete")} canEdit={canEdit} own={own} reload={reload}
            docTypes={docTypes.filter((t) => t.category === "skill")} />
        </TabsContent>

        {!own && <TabsContent value="confidential" className="mt-4">
          <ConfidentialTab staffName={name} isAdmin={can.has("hr.delete")} canEdit={can.has("hr.confidential")} docTypes={docTypes.filter((t) => t.category === "confidential")} />
        </TabsContent>}

        {!own && <TabsContent value="notes" className="mt-4">
          <NotesSection rows={form.notes || []} onChange={saveNotes} staffName={name} readOnly={!canEdit} />
        </TabsContent>}
      </Tabs>
    </div>
  );
}

function DocTable({ rows, staffName, testid, docTypes = [], category, isAdmin, canEdit, own, reload }) {
  const [versionOpen, setVersionOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [action, setAction] = useState(null); // { row, kind: 'delete' | 'archive' }
  const paged = usePaged(rows, 15, bySoonestExpiry);

  const byName = useMemo(() => {
    const m = {};
    for (const t of docTypes) m[t.name] = t;
    return m;
  }, [docTypes]);
  const optionsFor = (current) => {
    const opts = docTypes.map((t) => ({ value: t.name, label: t.name, hint: t.critical ? "critical" : (t.expiry_required ? "" : "no expiry") }));
    if (current && !docTypes.some((t) => t.name === current)) opts.unshift({ value: current, label: current, hint: "legacy" });
    return opts;
  };

  const view = async (url) => {
    try { const blob = await api.hrViewDocument(staffName, url); window.open(URL.createObjectURL(blob), "_blank"); }
    catch { toast.error("Could not open document."); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={`doctable-${testid}`}>
      <div className="divide-y divide-slate-100">
        {paged.slice.map(({ r, i }) => (
          <RecordCard key={r.name || i} r={r} idx={i} testid={testid} category={category} dt={byName[r.document_type]}
            isAdmin={isAdmin} canEdit={canEdit} own={own} staffName={staffName} view={view}
            onEdit={() => setEditRow(r)} onDelete={() => setAction({ row: r, kind: "delete" })}
            onArchive={() => setAction({ row: r, kind: "archive" })} />
        ))}
        {rows.length === 0 && <div className="px-3 py-6 text-center text-xs text-slate-400">Nothing recorded yet.</div>}
      </div>

      <Pager {...paged} testid={testid} />

      {canEdit && <div className="p-2.5 flex flex-wrap items-center gap-2 border-t border-slate-100">
        <Button size="sm" onClick={() => setVersionOpen(true)} style={{ backgroundColor: CRIMSON }} className="h-8 text-xs text-white" data-testid={`${testid}-add-version`}>
          <FilePlus className="h-3.5 w-3.5 mr-1" />Add / update document
        </Button>
        <span className="text-[10px] text-slate-400">Records lock once added. Upload a newer document to supersede one, or archive it if it lapses.</span>
      </div>}

      {!own && <ArchivedSection staffName={staffName} category={category} testid={testid} isAdmin={isAdmin} view={view} reload={reload} />}

      {versionOpen && (
        <DocVersionDialog staffName={staffName} category={category} testid={testid}
          docTypes={docTypes} byName={byName} optionsFor={optionsFor} currentRows={rows}
          onClose={() => setVersionOpen(false)} onDone={() => { setVersionOpen(false); reload(); }} />
      )}
      {editRow && (
        <RecordEditDialog staffName={staffName} category={category} testid={testid} row={editRow}
          optionsFor={optionsFor} onClose={() => setEditRow(null)} onDone={() => { setEditRow(null); reload(); }} />
      )}
      {action?.kind === "delete" && (
        <JustifyDialog testid={`${testid}-delete`} title="Delete document"
          message={`Deleting "${action.row.document_type}" is permanent and will be recorded in the audit trail.`}
          confirmText="Delete" tone="danger" required
          onClose={() => setAction(null)}
          onConfirm={async (j) => { await api.hrDocDelete(staffName, category, action.row.name, { justification: j }); toast.success("Document deleted."); setAction(null); reload(); }} />
      )}
      {action?.kind === "archive" && (
        <JustifyDialog testid={`${testid}-archive`} title="Archive document"
          message={`Move "${action.row.document_type}" to archived documents. It stays on file and an admin can restore it later.`}
          confirmText="Archive" tone="slate" label="Reason (optional)"
          onClose={() => setAction(null)}
          onConfirm={async (j) => { await api.hrDocArchiveActive(staffName, category, action.row.name, { reason: j }); toast.success("Document archived."); setAction(null); reload(); }} />
      )}
    </div>
  );
}

function Meta({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-slate-600 mt-0.5 break-words" data-testid={testid}>{value || "—"}</div>
    </div>
  );
}

function RecordCard({ r, idx, testid, category, dt, isAdmin, canEdit, own, staffName, view, onEdit, onDelete, onArchive }) {
  const { can } = useAuth();
  const canFix = !own && can.has("hr.edit_documents");
  const [showHistory, setShowHistory] = useState(false);
  const st = statusOf(r.expiry_date);
  return (
    <div className="p-3" data-testid={`${testid}-row-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-center gap-1.5">
          <span className="text-sm font-medium text-slate-800" data-testid={`${testid}-type-${idx}`}>{r.document_type || "—"}</span>
          {dt?.critical && <span title="Critical for rostering" className="text-[9px] font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded px-1 py-0.5 shrink-0">CRIT</span>}
        </div>
        <Badge className={`${STATUS_STYLE[st.key]} text-[9px] shrink-0`}>{st.key === "expired" ? "expired" : st.key === "expiring" ? `${st.days}d` : "current"}</Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2 mt-2 text-xs">
        <Meta label="Reference #" value={r.reference_number} testid={`${testid}-ref-${idx}`} />
        <Meta label="Issued" value={r.issue_date ? fmtDate(r.issue_date) : "—"} />
        <Meta label="Expiry" value={r.expiry_date ? fmtDate(r.expiry_date) : "—"} testid={`${testid}-expiry-${idx}`} />
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Document</div>
          {r.document_file ? (
            <button onClick={() => view(r.document_file)} className="text-[11px] text-rose-700 hover:underline flex items-center gap-1 mt-0.5" data-testid={`${testid}-view-${idx}`}><FileText className="h-3.5 w-3.5" />View</button>
          ) : <span className="text-slate-400">—</span>}
        </div>
        {category === "skill" && <Meta label="Training provider" value={r.training_provider} testid={`${testid}-provider-${idx}`} />}
      </div>
      <div className="flex items-center justify-between gap-2 mt-2.5 flex-wrap">
        <div className="text-[10px] text-slate-400">Added {fmtLogDate(r.added_at)}</div>
        <div className="flex items-center gap-2.5">
          {!own && <button onClick={() => setShowHistory((v) => !v)} className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid={`${testid}-history-${idx}`}><History className="h-3.5 w-3.5" />History</button>}
          {canEdit && <button onClick={onArchive} className="text-[11px] text-slate-500 hover:text-amber-700 flex items-center gap-1" data-testid={`${testid}-archive-btn-${idx}`}><Archive className="h-3.5 w-3.5" />Archive</button>}
          {canFix || isAdmin ? (
            <>
              {canFix && <button onClick={onEdit} className="text-[11px] text-slate-500 hover:text-rose-700 flex items-center gap-1" data-testid={`${testid}-edit-${idx}`}><Pencil className="h-3.5 w-3.5" />Edit</button>}
              {isAdmin && <button onClick={onDelete} className="text-[11px] text-slate-400 hover:text-rose-600 flex items-center gap-1" data-testid={`${testid}-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" />Delete</button>}
            </>
          ) : (
            <span className="text-[10px] text-slate-400 flex items-center gap-1" title="Locked — your role can't edit or delete these"><Lock className="h-3 w-3" />Locked</span>
          )}
        </div>
      </div>
      {showHistory && <EditHistory staffName={staffName} category={category} record={r.document_type} testid={`${testid}-${idx}`} />}
    </div>
  );
}

function EditHistory({ staffName, category, record, testid }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.hrDocAudit(staffName, category, record).then((d) => setItems(d.audit || [])).catch(() => setItems([])); }, [staffName, category, record]);
  return (
    <div className="mt-2 rounded-md bg-slate-50 border border-slate-100 p-2" data-testid={`${testid}-history-panel`}>
      {items === null ? <div className="flex justify-center py-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /></div>
        : items.length === 0 ? <div className="text-[11px] text-slate-400 text-center py-1">No changes recorded — this document is unchanged since it was added.</div>
        : <div className="space-y-1.5">{items.map((a) => (
            <div key={a.id} className="text-[11px] text-slate-600">
              <span className="font-medium capitalize">{a.action}</span> by {a.user_name} · {fmtLog(a.at)}
              {a.justification && <span className="text-slate-500"> — “{a.justification}”</span>}
              {(a.changes || []).map((c, ci) => <div key={ci} className="ml-3 text-[10px] text-slate-400">{c.field}: {String(c.old ?? "—")} → {String(c.new ?? "—")}</div>)}
            </div>))}</div>}
    </div>
  );
}

function JustifyDialog({ testid, title, message, confirmText, tone = "slate", label = "Justification", required = false, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (required && !reason.trim()) { toast.error("Please provide a justification."); return; }
    setBusy(true);
    try { await onConfirm(reason.trim()); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed."); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle className="text-base font-brand">{title}</DialogTitle></DialogHeader>
        <p className="text-[13px] text-slate-600 -mt-1">{message}</p>
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">{label}{required && <span className="text-rose-500"> *</span>}</Label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="w-full text-sm rounded-md border border-slate-200 p-2 min-h-[70px] mt-1" data-testid={`${testid}-reason`} placeholder={required ? "Explain why this change is necessary…" : "Optional"} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className={tone === "danger" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-slate-700 hover:bg-slate-800 text-white"} data-testid={`${testid}-confirm`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}{confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordEditDialog({ staffName, category, testid, row, optionsFor, onClose, onDone }) {
  const [type, setType] = useState(row.document_type || null);
  const [ref, setRef] = useState(row.reference_number || "");
  const [issue, setIssue] = useState(row.issue_date || "");
  const [expiry, setExpiry] = useState(row.expiry_date || "");
  const [provider, setProvider] = useState(row.training_provider || "");
  const [fileUrl, setFileUrl] = useState(row.document_file || null);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const doUpload = async (f) => {
    if (!f) return;
    setUploading(true);
    try { const res = await api.hrUploadDocument(staffName, f); setFileUrl(res.file_url); setFileName(f.name); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };
  const submit = async () => {
    if (!type) { toast.error("Choose a document type."); return; }
    if (!reason.trim()) { toast.error("A justification is required."); return; }
    setBusy(true);
    try {
      await api.hrDocEdit(staffName, category, row.name, {
        document_type: type, reference_number: ref || null, issue_date: issue || null,
        expiry_date: expiry || null, document_file: fileUrl || null,
        training_provider: category === "skill" ? (provider || null) : null,
        justification: reason.trim(),
      });
      toast.success("Document updated."); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid={`${testid}-edit-dialog`}>
        <DialogHeader><DialogTitle className="text-base font-brand">Edit document</DialogTitle></DialogHeader>
        <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-[12px] text-amber-800">Records are locked once added. Your change and justification are recorded in the audit trail.</div>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Document type</Label>
            <div className="mt-1"><Combobox options={optionsFor(type)} value={type} onChange={setType} placeholder="Select type…" testid={`${testid}-edit-type`} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px] font-semibold text-slate-600">Issued</Label><Input type="date" value={issue} onChange={(e) => setIssue(e.target.value)} className="h-9 mt-1 text-sm" data-testid={`${testid}-edit-issue`} /></div>
            <div><Label className="text-[11px] font-semibold text-slate-600">Expiry</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="h-9 mt-1 text-sm" data-testid={`${testid}-edit-expiry`} /></div>
          </div>
          <div><Label className="text-[11px] font-semibold text-slate-600">Reference #</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Optional" className="h-9 mt-1 text-sm" data-testid={`${testid}-edit-ref`} /></div>
          {category === "skill" && <div><Label className="text-[11px] font-semibold text-slate-600">Training provider</Label><Input value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Optional" className="h-9 mt-1 text-sm" data-testid={`${testid}-edit-provider`} /></div>}
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Document file</Label>
            <div className="mt-1 flex items-center gap-2">
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testid}-edit-file`} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-8 text-xs">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}Replace file
              </Button>
              {(fileName || fileUrl) && <span className="text-[11px] text-slate-500 truncate max-w-[220px]">{fileName || "current file kept"}</span>}
            </div>
          </div>
          <div><Label className="text-[11px] font-semibold text-slate-600">Justification <span className="text-rose-500">*</span></Label><textarea value={reason} onChange={(e) => setReason(e.target.value)} className="w-full text-sm rounded-md border border-slate-200 p-2 min-h-[64px] mt-1" data-testid={`${testid}-edit-reason`} placeholder="Explain why this change is necessary…" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} style={{ backgroundColor: CRIMSON }} className="text-white" data-testid={`${testid}-edit-save`}>{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function cmpNewer(a, b) {
  // Returns >0 if a is newer than b, <0 if older, 0 if equal. Issue date primary, expiry tiebreaker.
  const ai = a.issue_date || "", bi = b.issue_date || "";
  if (ai !== bi) return ai > bi ? 1 : -1;
  const ae = a.expiry_date || "", be = b.expiry_date || "";
  if (ae !== be) return ae > be ? 1 : -1;
  return 0;
}

function DocVersionDialog({ staffName, category, testid, docTypes, byName, optionsFor, currentRows, onClose, onDone }) {
  const [type, setType] = useState(null);
  const [ref, setRef] = useState("");
  const [provider, setProvider] = useState("");
  const [issue, setIssue] = useState("");
  const [expiry, setExpiry] = useState("");
  const [file, setFile] = useState(null); // {url, name}
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const dt = type ? byName[type] : null;
  // Only enforce expiry when the type is known AND explicitly requires it.
  // Defaulting true when dt is null would block uploads if docTypes failed to load,
  // and would incorrectly gate no-expiry document types (e.g. birth certificates).
  const expiryReq = !!(dt && dt.expiry_required);
  const current = currentRows.find((r) => (r.document_type || "").toLowerCase() === (type || "").toLowerCase());

  const doUpload = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const res = await api.hrUploadDocument(staffName, f);
      setFile({ url: res.file_url, name: f.name });
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };

  const commit = async (action) => {
    setBusy(true);
    try {
      await api.hrDocVersion(staffName, {
        category, document_type: type, reference_number: ref || null,
        issue_date: issue || null, expiry_date: expiry || null,
        document_file: file?.url || null, training_provider: provider || null, action,
      });
      toast.success(action === "archive_historical" ? "Filed to archived documents." : "Document saved as current.");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const submit = () => {
    if (!type) { toast.error("Choose a document type."); return; }
    if (expiryReq && !expiry) { toast.error("This document type requires an expiry date."); return; }
    const needFileNow = !current || !expiryReq || cmpNewer({ issue_date: issue, expiry_date: expiry }, current) > 0;
    if (needFileNow && !file?.url) { toast.error("Please upload the document file."); return; }
    if (!current) { commit("set_current"); return; }             // no existing → just set current
    if (!expiryReq) { setConfirm({ kind: "replace_noexp" }); return; } // no-expiry type → replace w/ confirm
    const c = cmpNewer({ issue_date: issue, expiry_date: expiry }, current);
    if (c > 0) setConfirm({ kind: "newer" });
    else setConfirm({ kind: "older" });
  };
  const [confirm, setConfirm] = useState(null);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid={`${testid}-version-dialog`}>
        <DialogHeader><DialogTitle className="text-base font-brand">Add / update document</DialogTitle></DialogHeader>
        {!confirm ? (
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Document type</Label>
              <div className="mt-1"><Combobox options={optionsFor(type)} value={type} onChange={setType} placeholder="Select type…" testid={`${testid}-version-type`} /></div>
              {current && <p className="text-[10px] text-amber-600 mt-1">There's already a current {type} — issued {current.issue_date || "—"}, expiry {current.expiry_date || "—"}.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] font-semibold text-slate-600">Issued / completed date</Label>
                <Input type="date" value={issue} onChange={(e) => setIssue(e.target.value)} className="h-9 mt-1 text-sm" data-testid={`${testid}-version-issue`} />
              </div>
              <div>
                <Label className="text-[11px] font-semibold text-slate-600">Expiry date {expiryReq && <span className="text-rose-500">*</span>}</Label>
                <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="h-9 mt-1 text-sm" data-testid={`${testid}-version-expiry`} />
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Reference #</Label>
              <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Optional" className="h-9 mt-1 text-sm" />
            </div>
            {category === "skill" && (
              <div>
                <Label className="text-[11px] font-semibold text-slate-600">Training provider</Label>
                <Input value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Optional" className="h-9 mt-1 text-sm" data-testid={`${testid}-version-provider`} />
              </div>
            )}
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Document file</Label>
              <div className="mt-1 flex items-center gap-2">
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testid}-version-file`} />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-8 text-xs">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}{file ? "Replace file" : "Upload file"}
                </Button>
                {file && <span className="text-[11px] text-slate-500 truncate max-w-[200px]">{file.name}</span>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} style={{ backgroundColor: CRIMSON }} className="text-white" data-testid={`${testid}-version-continue`}>Continue</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3" data-testid={`${testid}-version-confirm`}>
            {confirm.kind === "newer" && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[13px] text-amber-800">
                This document is <span className="font-semibold">newer</span> than the current {type}. Continuing will make it the current document and <span className="font-semibold">archive the existing one</span>.
              </div>
            )}
            {confirm.kind === "replace_noexp" && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[13px] text-amber-800">
                There's already a current {type}. Continuing will make this the current document and <span className="font-semibold">archive the existing one</span>.
              </div>
            )}
            {confirm.kind === "older" && (
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-[13px] text-slate-700">
                The dates entered make this <span className="font-semibold">older than the current {type}</span> (current: issued {current?.issue_date || "—"}, expiry {current?.expiry_date || "—"}). Please double-check the dates. If this really is an older document, you can file it straight to the archived section instead.
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setConfirm(null)} data-testid={`${testid}-version-back`}>Go back &amp; edit dates</Button>
              {confirm.kind === "older" ? (
                <Button onClick={() => commit("archive_historical")} disabled={busy} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid={`${testid}-version-historical`}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Archive className="h-4 w-4 mr-1.5" />}Upload as historical
                </Button>
              ) : (
                <Button onClick={() => commit("replace")} disabled={busy} style={{ backgroundColor: CRIMSON }} className="text-white" data-testid={`${testid}-version-replace`}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}Replace &amp; archive current
                </Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ArchivedSection({ staffName, category, testid, isAdmin, view, reload }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [restoring, setRestoring] = useState(null);

  const load = () => api.hrArchive(staffName, category).then((d) => setItems(d.archived || [])).catch(() => setItems([]));
  useEffect(() => { if (open && items === null) load(); }, [open]);

  const restore = async (a) => {
    if (!window.confirm(`Restore "${a.document_type}" as the current document? Whatever is currently active for this type will be archived.`)) return;
    setRestoring(a.id);
    try { await api.hrArchiveRestore(staffName, a.id); toast.success("Restored as current."); await reload(); setItems(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed."); }
    finally { setRestoring(null); }
  };

  return (
    <div className="border-t border-slate-100" data-testid={`${testid}-archive`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-semibold text-slate-500 hover:bg-slate-50" data-testid={`${testid}-archive-toggle`}>
        <Archive className="h-3.5 w-3.5" />Archived documents {items ? `(${items.length})` : ""}
        <ChevronDown className={`h-3.5 w-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        items === null ? <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div> :
        items.length === 0 ? <div className="px-3 py-4 text-center text-[11px] text-slate-400">No archived documents.</div> : (
          <div className="pb-2">
            {items.map((a) => (
              <div key={a.id} className="grid grid-cols-2 md:grid-cols-[1.3fr_0.8fr_0.8fr_0.8fr_auto] gap-2 px-3 py-2 border-t border-slate-50 items-center text-xs" data-testid={`${testid}-archive-row-${a.id}`}>
                <div className="font-medium text-slate-700">{a.document_type}</div>
                <div className="text-slate-500">{a.reference_number || "—"}</div>
                <div className="text-slate-500">iss {a.issue_date || "—"}</div>
                <div className="text-slate-500">exp {a.expiry_date || "—"}</div>
                <div className="flex items-center gap-2 justify-end">
                  {a.document_file && <button onClick={() => view(a.document_file)} className="text-[11px] text-rose-700 hover:underline flex items-center gap-1" data-testid={`${testid}-archive-view-${a.id}`}><FileText className="h-3.5 w-3.5" />View</button>}
                  {isAdmin && (
                    <button onClick={() => restore(a)} disabled={restoring === a.id} className="text-[11px] text-slate-500 hover:text-emerald-700 flex items-center gap-1" data-testid={`${testid}-archive-restore-${a.id}`}>
                      {restoring === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}Restore
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function ConfidentialTab({ staffName, isAdmin, canEdit, docTypes = [] }) {
  const [docs, setDocs] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ document_type: null, reference_number: "", note: "", document_file: null, file_name: "" });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [delRow, setDelRow] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.hrConfidential(staffName).then((d) => setDocs(d.documents || [])).catch(() => setDocs([]));
  useEffect(() => { load(); }, [staffName]);
  const paged = usePaged(docs || []);

  const options = docTypes.map((t) => ({ value: t.name, label: t.name }));
  const view = async (url) => {
    try {
      const blob = await api.hrViewDocument(staffName, url);
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Could not open document."); }
  };
  const doUpload = async (f) => {
    if (!f) return;
    setUploading(true);
    try { const res = await api.hrUploadDocument(staffName, f); setForm((s) => ({ ...s, document_file: res.file_url, file_name: f.name })); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };
  const save = async () => {
    if (!form.document_type) { toast.error("Choose a document type."); return; }
    if (!form.document_file) { toast.error("Please upload the document file."); return; }
    setBusy(true);
    try {
      await api.hrConfidentialCreate(staffName, { document_type: form.document_type, reference_number: form.reference_number || null, note: form.note || null, document_file: form.document_file || null });
      toast.success("Confidential document added.");
      setForm({ document_type: null, reference_number: "", note: "", document_file: null, file_name: "" });
      setAdding(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };
  const doDelete = async (d, justification) => {
    await api.hrConfidentialDelete(staffName, d.id, { justification });
    toast.success("Deleted."); setDelRow(null); load();
  };


  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="confidential-table">
      <div className="px-3 py-2.5 bg-slate-800 text-white flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-300" />
        <div className="text-xs font-semibold">Confidential — HR &amp; Admin only</div>
        <span className="text-[10px] text-slate-300 ml-2">Employment contracts, disciplinary records and other sensitive, non-expiring documents.</span>
      </div>

      {docs === null ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {docs.length === 0 && <div className="px-3 py-6 text-center text-xs text-slate-400" data-testid="confidential-empty">No confidential documents recorded.</div>}
          {docs.length > 0 && (
            <div className="hidden md:grid grid-cols-[1.2fr_0.8fr_1.2fr_0.8fr_auto] gap-2 px-3 py-2 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              <div>Document type</div><div>Reference #</div><div>Note</div><div>Added</div><div></div>
            </div>
          )}
          {paged.slice.map(({ r: d }) => (
            <ConfidentialRow key={d.id} d={d} staffName={staffName} isAdmin={isAdmin} canEdit={canEdit} view={view}
              onEdit={() => setEditRow(d)} onDelete={() => setDelRow(d)} />
          ))}

          <Pager {...paged} testid="confidential" />

          {adding ? (
            <div className="p-3 bg-slate-50/60 border-t border-slate-100 space-y-2" data-testid="confidential-add-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div><Label className="text-[11px] font-semibold text-slate-600">Document type</Label>
                  <div className="mt-1"><Combobox options={options} value={form.document_type} onChange={(v) => setForm((s) => ({ ...s, document_type: v }))} placeholder="Select type…" testid="confidential-type" /></div>
                </div>
                <div><Label className="text-[11px] font-semibold text-slate-600">Reference #</Label>
                  <Input value={form.reference_number} onChange={(e) => setForm((s) => ({ ...s, reference_number: e.target.value }))} className="h-9 mt-1 text-sm" placeholder="Optional" />
                </div>
              </div>
              <div><Label className="text-[11px] font-semibold text-slate-600">Note</Label>
                <Input value={form.note} onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))} className="h-9 mt-1 text-sm" placeholder="Optional context" />
              </div>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid="confidential-file" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-8 text-xs">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}{form.document_file ? "Replace file" : "Upload file"}
                </Button>
                {form.file_name && <span className="text-[11px] text-slate-500 truncate max-w-[200px]">{form.file_name}</span>}
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setAdding(false); setForm({ document_type: null, reference_number: "", note: "", document_file: null, file_name: "" }); }} className="h-8 text-xs">Cancel</Button>
                  <Button size="sm" onClick={save} disabled={busy} style={{ backgroundColor: CRIMSON }} className="h-8 text-xs text-white" data-testid="confidential-save">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}Save
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-2">
              <Button size="sm" onClick={() => setAdding(true)} style={{ backgroundColor: CRIMSON }} className="h-8 text-xs text-white" data-testid="confidential-add">
                <FilePlus className="h-3.5 w-3.5 mr-1" />Add confidential document
              </Button>
            </div>
          )}

          {editRow && (
            <ConfidentialEditDialog staffName={staffName} row={editRow} options={options}
              onClose={() => setEditRow(null)} onDone={() => { setEditRow(null); load(); }} />
          )}
          {delRow && (
            <JustifyDialog testid="confidential-delete" title="Delete confidential document"
              message={`Deleting "${delRow.document_type}" is permanent and will be recorded in the audit trail.`}
              confirmText="Delete" tone="danger" required
              onClose={() => setDelRow(null)} onConfirm={(j) => doDelete(delRow, j)} />
          )}
        </>
      )}
    </div>
  );
}

function ConfidentialRow({ d, staffName, isAdmin, canEdit, view, onEdit, onDelete }) {
  const [showHistory, setShowHistory] = useState(false);
  return (
    <div className="border-b border-slate-50 px-3 py-2.5" data-testid={`confidential-row-${d.id}`}>
      <div className="md:grid md:grid-cols-[1.2fr_0.8fr_1.2fr_0.8fr_auto] md:gap-2 md:items-center">
        <div className="text-sm font-medium text-slate-800">{d.document_type}</div>
        <div className="text-xs text-slate-500 mt-1 md:mt-0"><span className="md:hidden text-slate-400">Ref: </span>{d.reference_number || "—"}</div>
        <div className="text-xs text-slate-500 md:truncate mt-1 md:mt-0" title={d.note || ""}>{d.note || "—"}</div>
        <div className="text-xs text-slate-500 mt-1 md:mt-0"><span className="md:hidden text-slate-400">Added: </span>{fmtLogDate(d.added_at)}</div>
        <div className="flex items-center gap-3 md:justify-end mt-2 md:mt-0">
          {d.document_file && <button onClick={() => view(d.document_file)} className="text-[11px] text-rose-700 hover:underline flex items-center gap-1" data-testid={`confidential-view-${d.id}`}><FileText className="h-3.5 w-3.5" />View</button>}
          <button onClick={() => setShowHistory((v) => !v)} className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid={`confidential-history-${d.id}`}><History className="h-3.5 w-3.5" />History</button>
          {canEdit && (
            <button onClick={onEdit} className="text-[11px] text-slate-500 hover:text-rose-700 flex items-center gap-1" data-testid={`confidential-edit-${d.id}`}><Pencil className="h-3.5 w-3.5" />Edit</button>
          )}
          {isAdmin && (
            <button onClick={onDelete} className="text-[11px] text-slate-400 hover:text-rose-600 flex items-center gap-1" data-testid={`confidential-remove-${d.id}`}><Trash2 className="h-3.5 w-3.5" />Delete</button>
          )}
          {!canEdit && !isAdmin && (
            <span className="text-[10px] text-slate-400 flex items-center gap-1" title="Locked — only an HR Manager or Admin can edit"><Lock className="h-3 w-3" />Locked</span>
          )}
        </div>
      </div>
      {showHistory && <EditHistory staffName={staffName} category="confidential" record={d.document_type} testid={`confidential-${d.id}`} />}
    </div>
  );
}

function ConfidentialEditDialog({ staffName, row, options, onClose, onDone }) {
  const [type, setType] = useState(row.document_type || null);
  const [ref, setRef] = useState(row.reference_number || "");
  const [note, setNote] = useState(row.note || "");
  const [fileUrl, setFileUrl] = useState(row.document_file || null);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const doUpload = async (f) => {
    if (!f) return;
    setUploading(true);
    try { const res = await api.hrUploadDocument(staffName, f); setFileUrl(res.file_url); setFileName(f.name); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };
  const submit = async () => {
    if (!type) { toast.error("Choose a document type."); return; }
    if (!reason.trim()) { toast.error("A justification is required."); return; }
    setBusy(true);
    try {
      await api.hrConfidentialUpdate(staffName, row.id, {
        document_type: type, reference_number: ref || null, note: note || null,
        document_file: fileUrl || null, justification: reason.trim(),
      });
      toast.success("Confidential document updated."); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="confidential-edit-dialog">
        <DialogHeader><DialogTitle className="text-base font-brand">Edit confidential document</DialogTitle></DialogHeader>
        <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-[12px] text-amber-800">Records are locked once added. Your change and justification are recorded in the audit trail.</div>
        <div className="space-y-3">
          <div><Label className="text-[11px] font-semibold text-slate-600">Document type</Label><div className="mt-1"><Combobox options={options} value={type} onChange={setType} placeholder="Select type…" testid="confidential-edit-type" /></div></div>
          <div><Label className="text-[11px] font-semibold text-slate-600">Reference #</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Optional" className="h-9 mt-1 text-sm" data-testid="confidential-edit-ref" /></div>
          <div><Label className="text-[11px] font-semibold text-slate-600">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional context" className="h-9 mt-1 text-sm" data-testid="confidential-edit-note" /></div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Document file</Label>
            <div className="mt-1 flex items-center gap-2">
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid="confidential-edit-file" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-8 text-xs">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}Replace file
              </Button>
              {(fileName || fileUrl) && <span className="text-[11px] text-slate-500 truncate max-w-[220px]">{fileName || "current file kept"}</span>}
            </div>
          </div>
          <div><Label className="text-[11px] font-semibold text-slate-600">Justification <span className="text-rose-500">*</span></Label><textarea value={reason} onChange={(e) => setReason(e.target.value)} className="w-full text-sm rounded-md border border-slate-200 p-2 min-h-[64px] mt-1" data-testid="confidential-edit-reason" placeholder="Explain why this change is necessary…" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} style={{ backgroundColor: CRIMSON }} className="text-white" data-testid="confidential-edit-save">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const noteRows = (rows) => (rows || []).map((r) => ({
  note_type: r.note_type, date_time_of_note: r.date_time_of_note, subject: r.subject, note: r.note,
  document_file: r.document_file || null, document_file_name: r.document_file_name || null,
}));

function NotesSection({ rows, onChange, staffName, readOnly }) {
  const [types, setTypes] = useState([]);
  const [openIdx, setOpenIdx] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editIdx, setEditIdx] = useState(null);

  useEffect(() => {
    api.hrNoteTypes().then(setTypes).catch(() => {});
  }, []);

  const sorted = [...(rows || [])].map((r, i) => ({ ...r, _orig: i }))
    .sort((a, b) => (b.date_time_of_note || "").localeCompare(a.date_time_of_note || ""));

  const openNew = () => { setEditIdx(null); setShowDialog(true); };
  const openEdit = (origIdx, e) => { e.stopPropagation(); setEditIdx(origIdx); setShowDialog(true); };
  const remove = async (origIdx, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this note?")) return;
    if (await onChange(rows.filter((_, i) => i !== origIdx))) {
      setOpenIdx(null);
      toast.success("Note deleted.");
    }
  };

  const TYPE_COLOURS = {
    General: "bg-slate-100 text-slate-700 border-slate-200",
    Disciplinary: "bg-rose-50 text-rose-700 border-rose-200",
    Resignation: "bg-orange-50 text-orange-700 border-orange-200",
    Medical: "bg-blue-50 text-blue-700 border-blue-200",
    Performance: "bg-amber-50 text-amber-700 border-amber-200",
    Compliment: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const typeColour = (t) => TYPE_COLOURS[t] || "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <div className="space-y-2" data-testid="notes-table">
      {sorted.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-8 text-center text-xs text-slate-400">
          {readOnly ? "No notes yet." : <>No notes yet — click &ldquo;Add note&rdquo; to create the first one.</>}
        </div>
      )}
      {sorted.map((r) => {
        const isOpen = openIdx === r._orig;
        return (
          <div key={r._orig} className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`note-${r._orig}`}>
            <button type="button" onClick={() => setOpenIdx(isOpen ? null : r._orig)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              {r.note_type && (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${typeColour(r.note_type)}`}>{r.note_type}</span>
              )}
              <span className="flex-1 text-sm font-medium text-slate-700 truncate">
                {r.subject || <span className="text-slate-400 italic">Untitled</span>}
              </span>
              {r.document_file && <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" title="Has attachment" />}
              <span className="text-[11px] text-slate-400 shrink-0">
                {r.date_time_of_note ? fmtDate(r.date_time_of_note.split(" ")[0]) : "—"}
              </span>
              {!readOnly && <div className="flex items-center gap-1 ml-1" onClick={(e) => e.stopPropagation()}>
                <button onClick={(e) => openEdit(r._orig, e)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600" title="Edit" data-testid={`note-edit-${r._orig}`}><Pencil className="h-3 w-3" /></button>
                <button onClick={(e) => remove(r._orig, e)} className="p-1 rounded hover:bg-rose-50 text-slate-300 hover:text-rose-600" title="Delete" data-testid={`note-remove-${r._orig}`}><Trash2 className="h-3 w-3" /></button>
              </div>}
            </button>
            {isOpen && (
              <div className="border-t border-slate-100 px-4 py-3 space-y-3 bg-slate-50/40">
                {r.note ? (
                  <div className="prose prose-sm max-w-none text-sm text-slate-700 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-1 [&_b]:font-semibold [&_strong]:font-semibold"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(r.note) }} />
                ) : (
                  <p className="text-xs text-slate-400 italic">No note content.</p>
                )}
                {r.document_file && (
                  <div className="flex items-center gap-2 pt-1">
                    <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <button onClick={async () => {
                      try { const blob = await api.hrViewDocument(staffName, r.document_file); downloadBlob(blob, r.document_file_name || "attachment"); }
                      catch { toast.error("Could not download attachment."); }
                    }} className="text-xs text-blue-600 hover:underline truncate max-w-xs">
                      {r.document_file_name || "Download attachment"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <Button variant="outline" size="sm" onClick={openNew} className="h-8 text-xs" data-testid="note-add">
          <Plus className="h-3.5 w-3.5 mr-1" />Add note
        </Button>
      )}
      {showDialog && (
        <NoteDialog types={types} staffName={staffName}
          initial={editIdx !== null ? rows[editIdx] : null}
          onSave={async (note) => {
            const next = editIdx !== null ? rows.map((r, i) => i === editIdx ? note : r) : [...rows, note];
            if (await onChange(next)) { setShowDialog(false); toast.success("Note saved."); }
          }}
          onClose={() => setShowDialog(false)} />
      )}
    </div>
  );
}


function NoteDialog({ types, staffName, initial, onSave, onClose }) {
  const defaultDT = orgNowLocal().format("YYYY-MM-DD HH:mm");

  const [noteType, setNoteType] = useState(initial?.note_type || (types[0]?.label ?? ""));
  const [subject, setSubject] = useState(initial?.subject || "");
  const [note, setNote] = useState(initial?.note || "");
  const [dt, setDt] = useState(initial?.date_time_of_note || defaultDT);
  const [fileUrl, setFileUrl] = useState(initial?.document_file || null);
  const [fileName, setFileName] = useState(initial?.document_file_name || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!noteType && types.length > 0) setNoteType(types[0].label);
  }, [types]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const res = await api.hrUploadNoteDocument(staffName, f);
      setFileUrl(res.file_url); setFileName(f.name);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };

  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!subject.trim()) { toast.error("A subject/title is required."); return; }
    setSaving(true);
    await onSave({
      note_type: noteType || null, subject: subject.trim(), note: note || null,
      date_time_of_note: dt || defaultDT,
      document_file: fileUrl || null, document_file_name: fileName || null,
    });
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="note-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-brand">{initial ? "Edit note" : "Add note"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Category</Label>
              {types.length > 0 ? (
                <Select value={noteType} onValueChange={setNoteType}>
                  <SelectTrigger className="h-9 mt-1 text-sm" data-testid="note-dialog-type">
                    <SelectValue placeholder="Select category…" />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((t) => <SelectItem key={t.label} value={t.label} className="text-sm">{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={noteType} onChange={(e) => setNoteType(e.target.value)} placeholder="Category" className="h-9 mt-1 text-sm" data-testid="note-dialog-type-input" />
              )}
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Date &amp; time</Label>
              <Input type="datetime-local" value={dt ? dt.replace(" ", "T") : ""} onChange={(e) => setDt(e.target.value.replace("T", " "))} className="h-9 mt-1 text-sm" data-testid="note-dialog-dt" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Subject / Title <span className="text-rose-500">*</span></Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Performance review discussion" className="h-9 mt-1 text-sm" data-testid="note-dialog-subject" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Note</Label>
            <div className="mt-1 rounded-md border border-slate-200 overflow-hidden">
              <RichTextEditor value={note} onChange={setNote} placeholder="Add note content… (use toolbar for bold, lists etc.)" testid="note-dialog-body" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Attachment <span className="text-slate-400 font-normal">(optional)</span></Label>
            <div className="mt-1 flex items-center gap-2">
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="note-dialog-file" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-8 text-xs">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                {fileUrl ? "Replace file" : "Attach file"}
              </Button>
              {(fileName || fileUrl) && <span className="text-[11px] text-slate-500 truncate max-w-[200px]">{fileName || "current file kept"}</span>}
              {fileUrl && <button onClick={() => { setFileUrl(null); setFileName(""); }} className="text-rose-400 hover:text-rose-600 text-[11px]">Remove</button>}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={onClose} className="h-8 text-xs">Cancel</Button>
          <Button size="sm" onClick={save} disabled={uploading || saving} style={{ backgroundColor: CRIMSON }} className="h-8 text-xs text-white" data-testid="note-dialog-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}Save note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AddStaffDialog({ onClose, onCreated }) {
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const today = todayISO();
  const [f, setF] = useState({
    first_name: "", last_name: "", gender: "", date_of_birth: "",
    date_of_joining: today, status: "Active", company: "",
    designation: "", reports_to: "", company_email: "", cell_number: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.hrMeta().then((m) => {
      setMeta(m);
      setF((p) => ({ ...p, company: m.companies?.[0] || "" }));
    }).catch(() => toast.error("Could not load form options."));
  }, []);

  const valid = f.first_name.trim() && f.gender && f.date_of_birth && f.date_of_joining
    && f.company && f.designation && f.reports_to;

  const submit = async () => {
    if (!valid) { toast.error("Please fill in the required fields."); return; }
    setSaving(true);
    try {
      const res = await api.hrCreateStaff(f);
      toast.success("Staff created — opening their profile.");
      onCreated(res.employee);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create staff."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="add-staff-dialog">
        <DialogHeader><DialogTitle className="font-brand">Add staff</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Creates an Employee (HR) record — the source of truth for staff.</p>
        {!meta ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3 py-1">
            <Field label="First name *">
              <Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-9 text-sm" data-testid="add-first-name" />
            </Field>
            <Field label="Last name">
              <Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-9 text-sm" data-testid="add-last-name" />
            </Field>
            <Field label="Gender *">
              <Select value={f.gender} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="add-gender"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{meta.genders.map((g) => <SelectItem key={g} value={g} className="text-sm">{g}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="add-status"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.statuses.map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Date of birth *">
              <Input type="date" value={f.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="h-9 text-sm" data-testid="add-dob" />
            </Field>
            <Field label="Date of joining *">
              <Input type="date" value={f.date_of_joining} onChange={(e) => set("date_of_joining", e.target.value)} className="h-9 text-sm" data-testid="add-doj" />
            </Field>
            <Field label="Company *">
              <Select value={f.company} onValueChange={(v) => set("company", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="add-company"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{meta.companies.map((c) => <SelectItem key={c} value={c} className="text-sm">{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Designation *">
              <Combobox testid="add-designation" placeholder="Select role…"
                options={meta.designations.map((d) => ({ value: d, label: d }))}
                value={f.designation} onChange={(v) => set("designation", v || "")} />
            </Field>
            <Field label="Reports to *" full>
              <Combobox testid="add-reports-to" placeholder="Select manager…"
                options={meta.employees.map((e) => ({ value: e.name, label: e.employee_name, hint: e.name }))}
                value={f.reports_to} onChange={(v) => set("reports_to", v || "")} />
            </Field>
            <Field label="Mobile">
              <Input value={f.cell_number} onChange={(e) => set("cell_number", e.target.value)} className="h-9 text-sm" data-testid="add-cell" />
            </Field>
            <Field label="Work email" full>
              <Input type="email" value={f.company_email} onChange={(e) => set("company_email", e.target.value)} className="h-9 text-sm" data-testid="add-email" />
            </Field>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="add-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !meta} style={{ backgroundColor: CRIMSON }} className="text-white" data-testid="add-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />}Create staff
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-[11px] text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
